import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, Clock, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = ['all', 'Planning', 'Decor', 'Beauty', 'Trends', 'Rituals'];

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setPosts(response.data || []);
    } catch (error) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  // Additional static posts for display
  const staticPosts = [
    {
      id: 'static-1',
      title: 'How to Choose the Perfect Wedding Venue in Canada',
      excerpt: 'From intimate gardens to grand ballrooms, discover how to find a venue that matches your vision and budget.',
      category: 'Planning',
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800',
      author: 'Priya Sharma',
      created_at: new Date().toISOString(),
    },
    {
      id: 'static-2',
      title: 'Top Bridal Lehenga Trends for 2024',
      excerpt: 'From pastels to bold reds, explore the hottest bridal fashion trends making waves this wedding season.',
      category: 'Trends',
      image: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=800',
      author: 'Meera Patel',
      created_at: new Date().toISOString(),
    },
    {
      id: 'static-3',
      title: 'Essential Sangeet Night Planning Guide',
      excerpt: 'Make your sangeet unforgettable with these tips on entertainment, decor, and creating the perfect vibe.',
      category: 'Planning',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
      author: 'Anjali Singh',
      created_at: new Date().toISOString(),
    },
    {
      id: 'static-4',
      title: 'Understanding Hindu Wedding Rituals',
      excerpt: 'A comprehensive guide to traditional Hindu wedding ceremonies and their beautiful significance.',
      category: 'Rituals',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800',
      author: 'Raj Kumar',
      created_at: new Date().toISOString(),
    },
    {
      id: 'static-5',
      title: 'Budget-Friendly Wedding Decor Ideas',
      excerpt: 'Create stunning wedding decor without breaking the bank with these creative DIY ideas.',
      category: 'Decor',
      image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800',
      author: 'Simran Kaur',
      created_at: new Date().toISOString(),
    },
    {
      id: 'static-6',
      title: 'Pre-Wedding Skincare Routine for Brides',
      excerpt: 'Get that bridal glow with this expert-recommended skincare routine starting 6 months before your wedding.',
      category: 'Beauty',
      image: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=800',
      author: 'Dr. Nisha Verma',
      created_at: new Date().toISOString(),
    },
  ];

  const allPosts = [...posts, ...staticPosts].filter(
    post => activeCategory === 'all' || post.category === activeCategory
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
              Wedding <span className="gradient-text">Blog</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Expert tips, trends, and inspiration for your South Asian wedding
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((category) => (
            <Button
              key={category}
              variant={activeCategory === category ? 'default' : 'outline'}
              onClick={() => setActiveCategory(category)}
              className={`rounded-full capitalize ${
                activeCategory === category 
                  ? 'bg-primary text-white' 
                  : 'border-pink-200 hover:bg-pink-50'
              }`}
              data-testid={`blog-category-${category}`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Featured Post */}
        {allPosts.length > 0 && (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="grid lg:grid-cols-2 gap-8 bg-white rounded-3xl overflow-hidden border border-pink-100 hover:border-pink-200 transition-all group cursor-pointer">
              <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
                <img
                  src={allPosts[0].image}
                  alt={allPosts[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <Badge className="w-fit bg-pink-100 text-pink-600 hover:bg-pink-100 mb-4">
                  {allPosts[0].category}
                </Badge>
                <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-4 group-hover:text-pink-500 transition-colors">
                  {allPosts[0].title}
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  {allPosts[0].excerpt}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {allPosts[0].author}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    5 min read
                  </span>
                </div>
                <Button className="w-fit rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 gap-2">
                  Read More <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.article>
        )}

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse">
                <div className="aspect-[16/10] bg-pink-100" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-pink-100 rounded w-1/4" />
                  <div className="h-6 bg-pink-100 rounded w-3/4" />
                  <div className="h-4 bg-pink-50 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allPosts.slice(1).map((post, idx) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-3xl overflow-hidden border border-pink-50 hover:border-pink-200 transition-all hover:shadow-soft group cursor-pointer"
                data-testid={`blog-post-${post.id}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <Badge className="bg-pink-50 text-pink-600 hover:bg-pink-100 mb-3">
                    {post.category}
                  </Badge>
                  <h3 className="font-heading font-bold text-lg mb-3 group-hover:text-pink-500 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      5 min
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-16 bg-gradient-to-r from-primary to-secondary rounded-3xl p-8 lg:p-12 text-center text-white">
          <h3 className="font-heading text-2xl lg:text-3xl font-bold mb-4">
            Get Wedding Tips in Your Inbox
          </h3>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Subscribe to our newsletter for the latest wedding trends, tips, and exclusive content.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-full text-foreground"
            />
            <Button className="bg-white text-pink-500 hover:bg-white/90 rounded-full px-8">
              Subscribe
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
